import React, { useEffect, useState } from 'react';
import { fetchFamilyRecapsForStudent } from '../../services/LessonRecapService';
import { groupRecapsByLesson, TIER_LABEL, RECAP_VISIBILITY } from '../../services/lessonRecapModel';
import RichTextView from './RichTextView';

/* StudentRecaps — the family-facing READ surface for Lesson Recaps.
   Arc item 5, second half (the Song Notes half shipped 2026-08-03).

   WHERE THIS LIVES AND WHY IT IS NOT SOURCED FROM `lessons`. Anthony chose
   "on the lesson, under Schedule". The `lessons` collection cannot supply that:
   its customer read branch keys on `resource.data.userId == request.auth.uid`
   (firestore.rules), a legacy per-user field that NONE of the 1,517 MMS-imported
   lesson docs carry — so that branch matches zero documents — and MyLessons
   never queries `lessons` at all, rendering forward-looking occurrences off
   `timeSlots`. The recap docs already carry `lessonId`, `studentId` and
   `createdAt`, so the past-lesson list is built from the recaps themselves.
   Same placement, no rules change, no legacy field dragged into scope.

   WHAT A FAMILY MAY SEE IS DECIDED BY `visibility`, NOT BY THIS FILE. The
   `teacher` tier is excluded in the Firestore rule, in the query, and in
   familyVisibleRecaps() inside groupRecapsByLesson. This component adds no
   fourth filter of its own — a UI filter is not a wall.

   READ-ONLY, permanently. Recaps are teacher-write-only; there is no write path
   from a family into teacher-authored feedback and none should be added here. */

const INITIAL_RECAPS = 3;

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const recapDateLabel = (d) => {
	if (!d) return 'Lesson';
	const now = new Date();
	const sameYear = d.getFullYear() === now.getFullYear();
	return `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}${sameYear ? '' : `, ${d.getFullYear()}`}`;
};

const TierBlock = ({ recap, showLabel }) => (
	<div style={{ marginTop: showLabel ? '0.7rem' : 0 }}>
		{showLabel && (
			<div
				style={{
					fontSize: '0.66rem',
					fontWeight: 700,
					letterSpacing: '0.08em',
					textTransform: 'uppercase',
					// teal-800, not teal-600: measured 2026-09-22 on the card, teal-600
					// is 2.65:1 light and 2.39:1 Comfort Dark (fails AA for 10.5px
					// text); teal-800 is 5.42 / 6.59 / 6.97 across the three themes.
					color: 'var(--cyv-teal-800, #2a7570)',
					marginBottom: '0.3rem',
				}}>
				{TIER_LABEL[recap.visibility] || 'Note'}
			</div>
		)}
		<RichTextView content={recap.content} fallback={recap.plainText} />
	</div>
);

/* Two presentation props, both defaulting to the original behavior so the
   in-card usage and its test are unchanged:

   showHeading  — the "Past lessons" section label. Suppressed when the caller
                  is already a surface named "Past lessons" (the Schedule
                  page's view tabs), where repeating it under every student
                  name is noise rather than orientation.
   emptyMessage — what to render when a student has no family-visible recaps.
                  Inside a card, null is right: the section self-hides rather
                  than putting an empty box on every card. Inside a TAB the
                  same silence reads as a broken page, because the parent
                  chose to come here. Today that is the near-universal case —
                  exactly one student in the studio has any recap a family
                  can read — so this is the state most parents will meet. */
/* studentOnly  — the signed-in principal IS a student (P2b), whose rule admits
                  the `student` tier only. Must reach the query: see
                  visibilitiesFor() for why asking for both is a denial, not a
                  trim.
   initialCount — how many lessons show before "Show all" (3 in a card; the
                  Lesson Recaps page shows more). */
const StudentRecaps = ({
	studentId,
	studentName,
	showHeading = true,
	emptyMessage = null,
	studentOnly = false,
	initialCount = INITIAL_RECAPS,
}) => {
	const [recaps, setRecaps] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [showAll, setShowAll] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (!studentId) {
			setLoading(false);
			return undefined;
		}
		(async () => {
			setLoading(true);
			setError(false);
			try {
				const rows = await fetchFamilyRecapsForStudent(studentId, undefined, { studentOnly });
				if (!cancelled) setRecaps(rows);
			} catch (e) {
				// A LOAD FAILURE MUST NOT READ AS "no recaps yet". Silence here
				// would tell a parent their teacher wrote nothing, which is a
				// different and worse claim than "we couldn't load this".
				console.error('Failed to load lesson recaps', e);
				if (!cancelled) setError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [studentId, studentOnly]);

	if (loading) {
		return (
			<div className='cust-sched-uplabel' style={{ marginTop: '1rem' }}>
				{showHeading && 'Past lessons'}
				<div style={{ fontWeight: 400, color: '#777', marginTop: '0.3rem' }}>Loading…</div>
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ marginTop: '1rem' }}>
				{showHeading && <div className='cust-sched-uplabel'>Past lessons</div>}
				<p style={{ color: '#a33', margin: '0.3rem 0 0', fontSize: '0.9rem' }}>
					We couldn't load lesson recaps just now. Please try again in a moment.
				</p>
			</div>
		);
	}

	const groups = groupRecapsByLesson(recaps);
	// Nothing written yet is the NORMAL state today, not an error — the surface
	// ships ahead of teachers writing recaps at family-visible tiers. Render
	// nothing rather than an empty box on every card, UNLESS the caller asked
	// for an explicit empty state (see the prop note above).
	if (!groups.length) {
		if (!emptyMessage) return null;
		return (
			<div style={{ marginTop: '0.25rem' }}>
				{showHeading && <div className='cust-sched-uplabel'>Past lessons</div>}
				<p className='cust-sched-empty'>{emptyMessage}</p>
			</div>
		);
	}

	const shown = showAll ? groups : groups.slice(0, initialCount);

	return (
		<div style={{ marginTop: '1rem' }}>
			{showHeading && <div className='cust-sched-uplabel'>Past lessons</div>}
			<ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
				{shown.map((group) => {
					// Label the tiers only when BOTH are present: a lone recap needs
					// no heading, but two unlabelled blocks under one date read as a
					// duplicate rather than as "and this part is for you".
					const multiTier = group.tiers.length > 1;
					return (
						<li
							key={group.key}
							style={{
								// Web Awesome's native list styles give every <li> an 18px
								// start margin from a layer; only DECLARING margin beats it
								// (CLAUDE.md → native.css). Measured 2026-09-22: every recap
								// sat 18px inset from its own student's name.
								margin: 0,
								padding: '0.7rem 0',
								borderTop: '1px solid var(--wa-color-surface-border, #eee)',
								// See RichTextView: App.js:151 centres the whole tree and
								// nothing under the portal resets it. The date heading and
								// tier label are block-level too, so they need this as much
								// as the prose does. The "Past lessons" section label above
								// deliberately does NOT get it — it stays centred to match
								// the existing "Upcoming" label it sits beneath.
								textAlign: 'left',
							}}>
							<div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
								<i
									className='fa-sharp fa-solid fa-comment-lines'
									style={{ marginRight: '0.5rem', color: 'var(--cyv-teal-600, #54ada7)' }}></i>
								{recapDateLabel(group.date)}
								{group.authorFirstName && (
									<span style={{ fontWeight: 400, color: 'var(--text-muted, #5f6873)' }}>
										{' '}· with {group.authorFirstName}
									</span>
								)}
							</div>
							<div style={{ fontSize: '0.93rem', color: 'var(--text-body, #3f3a34)' }}>
								{group.tiers.map((recap) => (
									<TierBlock
										key={recap.id}
										recap={recap}
										showLabel={multiTier || recap.visibility === RECAP_VISIBILITY.PARENT}
									/>
								))}
							</div>
						</li>
					);
				})}
			</ul>
			{groups.length > initialCount && (
				<button
					type='button'
					className='cust-sched-more'
					aria-expanded={showAll}
					onClick={() => setShowAll((v) => !v)}>
					{showAll
						? 'Show fewer'
						: `Show all ${groups.length} lesson recaps${studentName ? ` for ${studentName}` : ''}`}
				</button>
			)}
		</div>
	);
};

export default StudentRecaps;
